import { Spacing } from '@persona-id/design-tokens';
import { useState, VFC } from 'react';
import styled from 'styled-components';

import FormControlLabel from 'dashboard/components/FormControls/FormControlLabel';
import ReactSelect, { OptionType } from 'dashboard/components/ReactSelect';
import ExpressionTerm from 'dashboard/components/WorkflowControlPanel/StepConfigAction/ExpressionEditor/Term';
import {
  ExpressionRuleValueAll,
  ExpressionRuleValueConfig,
  getVariableAllowedArrayTypes,
  getVariableAllowedTypes,
  RuleValueTypes,
} from 'dashboard/lib/workflows';
import { WorkflowOperatorKey } from 'dashboard/models/workflow';
import { VariableType } from 'dashboard/models/workflow-version';
import { BASE } from 'design-system/variables/spacing';
import { JsonSchema } from 'lib/json-schema';

export const Row = styled.div`
  display: flex;
  align-items: center;
`;

export const SideLabel = styled(FormControlLabel)`
  margin-bottom: 0;
  margin-right: ${Spacing[2]};
  width: ${BASE * 8}px;
`;

const InputContainer = styled.div`
  flex: 1;
`;

export const ReactSelectContainer = styled.div`
  width: ${BASE * 15}px;
  margin-right: ${Spacing[2]};
`;

export const REACT_SELECT_STYLES = {
  indicatorsContainer: () => ({
    marginRight: `${Spacing[2]}`,
  }),
};

interface TermConfigProps {
  id: string;
  hasError?: boolean;
  jsonSchema: JsonSchema;
  label: string;
  operator: WorkflowOperatorKey;
  isDefault?: boolean;
  termConfig: ExpressionRuleValueConfig;
  termOptions: OptionType<RuleValueTypes>[];
  tokensToDisplayValue: Record<string, string>;
  variableSchema: JsonSchema;
  variableType: VariableType;
  varTokensToDisplayValue: Record<string, string>;
  setVariableValue: (value: ExpressionRuleValueAll) => void;
}

const TermConfig: VFC<TermConfigProps> = ({
  id,
  hasError,
  jsonSchema,
  label,
  operator,
  isDefault,
  termConfig,
  termOptions,
  tokensToDisplayValue,
  variableSchema,
  variableType,
  varTokensToDisplayValue,
  setVariableValue,
}) => {
  const [termType, setTermType] = useState(termConfig?.type || RuleValueTypes.Field);

  const onTermTypeChange = (newTermType: RuleValueTypes) => {
    setTermType(newTermType);
    setVariableValue(null);
  };

  return (
    <Row>
      <SideLabel>{label}</SideLabel>
      <ReactSelectContainer>
        <ReactSelect<RuleValueTypes>
          value={termType}
          options={termOptions}
          styles={REACT_SELECT_STYLES}
          onChange={onTermTypeChange}
        />
      </ReactSelectContainer>
      <InputContainer>
        <ExpressionTerm
          allowedTypes={getVariableAllowedTypes(
            variableType,
            isDefault ? WorkflowOperatorKey.OrAssign : operator,
          )}
          allowedArrayTypes={getVariableAllowedArrayTypes(
            isDefault ? WorkflowOperatorKey.OrAssign : operator,
          )}
          id={id}
          hasError={hasError}
          variableType={variableType}
          term={termConfig?.value}
          termType={termType}
          jsonSchema={jsonSchema}
          operator={operator}
          tokensToDisplayValue={tokensToDisplayValue}
          variableSchema={variableSchema}
          varTokensToDisplayValue={varTokensToDisplayValue}
          setVariableValue={setVariableValue}
        />
      </InputContainer>
    </Row>
  );
};

export default TermConfig;
